import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { AxiosError } from 'axios';
import { Plus, Trash2, Globe } from 'lucide-react';
import Modal from '../common/Modal';
import Button from '../common/Button';
import ConfigEditor from './ConfigEditor';
import { Embed, EmbedSettings, UpdateEmbedInput } from '../../types';
import { isValidDomain } from '../../utils/validators';
import { useToast, useEmbeds } from '../../hooks';

const editSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100, 'Name is too long'),
});

type FormData = z.infer<typeof editSchema>;

interface EditEmbedModalProps {
  open: boolean;
  onClose: () => void;
  embed: Embed | null;
}

/**
 * EditEmbedModal allows updating an embed's name, allowed domains, settings, and active status.
 */
const EditEmbedModal: React.FC<EditEmbedModalProps> = ({ open, onClose, embed }) => {
  const { updateEmbed } = useEmbeds();
  const { showToast } = useToast();
  const [settings, setSettings] = useState<EmbedSettings | null>(null);
  const [domains, setDomains] = useState<string[]>([]);
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Default settings structure
  const defaultSettings: EmbedSettings = {
    lighting: {
      preset: 'neutral',
      intensity: 1.0,
    },
    camera: {
      fov: 45,
      initialViewpoint: 'front',
      autoFrame: true,
    },
    environment: {
      background: 'transparent',
      backgroundColor: '#ffffff',
      envMapPreset: 'studio',
    },
    controls: {
      enableRotate: true,
      enableZoom: true,
      enablePan: false,
      autoRotate: false,
      autoRotateSpeed: 2,
    },
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(editSchema),
  });

  // Populate form when embed changes
  useEffect(() => {
    if (embed) {
      console.log('📝 EditEmbedModal - Populating form with embed:', embed);
      reset({ name: embed.name });
      
      // Merge embed settings with defaults to ensure all properties exist
      const mergedSettings: EmbedSettings = {
        lighting: {
          ...defaultSettings.lighting,
          ...(embed.settings?.lighting || {}),
        },
        camera: {
          ...defaultSettings.camera,
          ...(embed.settings?.camera || {}),
        },
        environment: {
          ...defaultSettings.environment,
          ...(embed.settings?.environment || {}),
        },
        controls: {
          ...defaultSettings.controls,
          ...(embed.settings?.controls || {}),
        },
      };
      
      setSettings(mergedSettings);
      setDomains(embed.allowedDomains.length ? embed.allowedDomains : ['']);
      setIsActive(embed.isActive);
    }
  }, [embed, reset]);

  if (!embed) {
    console.log('⚠️  EditEmbedModal - No embed provided, returning null');
    return null;
  }

  console.log('✅ EditEmbedModal - Rendering modal with embed:', embed.id);

  const handleAddDomain = () => setDomains((prev) => [...prev, '']);

  const handleUpdateDomain = (idx: number, value: string) => {
    const copy = [...domains];
    copy[idx] = value;
    setDomains(copy);
  };

  const handleRemoveDomain = (idx: number) => {
    setDomains((prev) => prev.filter((_, i) => i !== idx || prev.length === 1));
  };

  const onSubmit = async (data: FormData) => {
    if (!embed) return;
    setIsSubmitting(true);
    try {
      const allowedDomains = domains.map((d) => d.trim()).filter(Boolean);
      if (!allowedDomains.length) {
        showToast('error', 'Please provide at least one allowed domain');
        setIsSubmitting(false);
        return;
      }
      const invalid = allowedDomains.filter((d) => !isValidDomain(d));
      if (invalid.length) {
        showToast('error', `Invalid domain(s): ${invalid.join(', ')}`);
        setIsSubmitting(false);
        return;
      }

      const input: UpdateEmbedInput = {
        name: data.name,
        allowedDomains,
        settings: settings ?? defaultSettings,
        isActive,
      };

      await updateEmbed(embed.id, input);
      showToast('success', 'Embed updated');
      onClose();
    } catch (error: unknown) {
      showToast('error', (error as AxiosError<{ message?: string }>).response?.data?.message || 'Failed to update embed');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={`Edit: ${embed.name}`} size="large">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Embed Name <span className="text-red-500">*</span>
            </label>
            <input
              {...register('name')}
              type="text"
              className="input-field"
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Allowed Domains <span className="text-red-500">*</span>
              </label>
              <span className="text-xs text-gray-500">
                {domains.filter(d => d.trim()).length} {domains.filter(d => d.trim()).length === 1 ? 'domain' : 'domains'}
              </span>
            </div>
            
            {/* Currently Allowed Domains Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex items-center gap-2 mb-3">
                <Globe className="h-4 w-4 text-blue-600" />
                <h4 className="text-sm font-semibold text-blue-900">
                  Currently Allowed Domains
                </h4>
              </div>
              
              {embed.allowedDomains.length > 0 ? (
                <div className="space-y-2">
                  {embed.allowedDomains.map((domain, idx) => (
                    <div 
                      key={`original-${idx}`} 
                      className="flex items-center gap-2 text-sm"
                    >
                      <div className="h-2 w-2 rounded-full bg-blue-500" />
                      <span className="text-blue-800 font-mono">{domain}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-blue-700 italic">
                  No domains currently configured
                </p>
              )}
            </div>
            
            {/* Edit Domains Section */}
            <div className="space-y-3">
              <h4 className="text-sm font-medium text-gray-700">
                Update Allowed Domains
              </h4>
              <div className="space-y-2">
                {domains.map((domain, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <input
                      value={domain}
                      onChange={(e) => handleUpdateDomain(idx, e.target.value)}
                      className="input-field flex-1"
                      placeholder="example.com or *.example.com"
                      disabled={isSubmitting}
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => handleRemoveDomain(idx)}
                      disabled={domains.length === 1 || isSubmitting}
                      className="flex items-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
              
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleAddDomain}
                disabled={isSubmitting}
                className="flex items-center gap-1"
              >
                <Plus className="h-3 w-3" />
                Add Another Domain
              </Button>
              
              <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong>Supported formats:</strong>
                </p>
                <ul className="text-xs text-gray-600 mt-1 space-y-1 ml-4 list-disc">
                  <li><span className="font-mono">example.com</span> - Exact domain match</li>
                  <li><span className="font-mono">*.example.com</span> - All subdomains</li>
                  <li><span className="font-mono">localhost:3000</span> - Development server</li>
                  <li><span className="font-mono">192.168.1.1:8080</span> - IP address with port</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="isActive"
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              disabled={isSubmitting}
            />
            <label htmlFor="isActive" className="text-sm text-gray-700">
              Embed is active
            </label>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-2">Viewer Settings</h4>
          <ConfigEditor
            settings={settings || embed.settings}
            onChange={(next) => setSettings(next)}
          />
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting}>
            Save Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default EditEmbedModal;
